(function () {
    'use strict';

    /**
     * Remembers what each capture was started for.
     *
     * THANK-692: a recording is only posted once its upload finishes, and the
     * recipients used to be read from live UI state at that moment. Any
     * selection that moved while the upload was in flight silently re-targeted
     * the video.
     *
     * Captures are tracked as a map rather than a single slot because a recorder
     * can start a new recording while an earlier upload is still running --
     * selecting another recipient mid-upload reopens the camera. Each capture
     * keeps its own recipients and its own kind (a personal video or the
     * campaign add-on), so every recording is saved as what it was recorded as,
     * for the people it was recorded for, and none is dropped.
     */
    angular.module('thankview.recorder')
        .factory('recorderTargetService', [function () {
            // a capture is only released when its video is saved, so keep a
            // ceiling on how many an abandoned-heavy session can accumulate
            var MAX_CAPTURES = 20;

            var recorderTargetService = {};
            var captures = {};
            var order = [];
            var currentToken = null;
            var captureCount = 0;

            var mapIds = function (guests) {
                return (guests || []).map(function (guest) {
                    return guest.id;
                });
            };

            /**
             * Drop the oldest captures once over the ceiling, but never one
             * whose upload is still running -- that would lose the recording
             * this service exists to protect. If every candidate is uploading
             * the map is left oversized until they finish.
             */
            var evict = function () {
                while (order.length > MAX_CAPTURES) {
                    var evictable = null;

                    for (var i = 0; i < order.length; i++) {
                        // never the capture that just started, and never one
                        // whose recording is still on its way to the server
                        if (order[i] !== currentToken && !captures[order[i]].uploading) {
                            evictable = order[i];
                            break;
                        }
                    }

                    if (!evictable) {
                        return;
                    }

                    delete captures[evictable];
                    order.splice(order.indexOf(evictable), 1);
                }
            };

            /**
             * Pin what a capture is starting for and return a token identifying
             * it. The token travels with the recording through its upload so the
             * result can be matched back to this capture.
             *
             * @param  {Array}   guestIds   : recipients, for a personal video
             * @param  {boolean} isPersonal : false for the campaign add-on video
             * @return {string} token for this capture
             */
            recorderTargetService.begin = function (guestIds, isPersonal) {
                captureCount += 1;

                var token = 'capture-' + captureCount;

                captures[token] = {
                    guestIds: (guestIds || []).slice(),
                    isPersonal: isPersonal !== false,
                    uploading: false,
                };

                order.push(token);
                currentToken = token;

                evict();

                return token;
            };

            /**
             * Token of the capture that started most recently, or null when
             * nothing has been pinned yet.
             */
            recorderTargetService.getToken = function () {
                return currentToken;
            };

            /**
             * Recipient ids pinned for a token, or for the current capture when
             * no token is given. Empty when that capture is unknown.
             */
            recorderTargetService.getGuestIds = function (token) {
                var capture = captures[token || currentToken];

                return capture ? capture.guestIds.slice() : [];
            };

            /**
             * Note that a capture's upload has started, so it is not evicted
             * while its recording is still in flight.
             */
            recorderTargetService.markUploading = function (token) {
                var capture = captures[token || currentToken];

                if (capture) {
                    capture.uploading = true;
                }
            };

            /**
             * Note that a capture's upload is over without releasing it -- a save
             * that failed keeps its recipients for a retry, but the recording is
             * no longer in flight, so the capture can be evicted again rather
             * than pinning the map open for the rest of the session.
             */
            recorderTargetService.markUploadSettled = function (token) {
                var capture = captures[token];

                if (capture) {
                    capture.uploading = false;
                }
            };

            /**
             * Forget a capture once its video has been saved. Abandoned captures
             * are left in place -- nothing reads them, and the ceiling bounds
             * how many are kept.
             */
            recorderTargetService.release = function (token) {
                if (!captures[token]) {
                    return;
                }

                delete captures[token];
                order.splice(order.indexOf(token), 1);

                // don't leave the current token pointing at a capture that is
                // no longer on record
                if (currentToken === token) {
                    currentToken = null;
                }
            };

            /**
             * Forget every capture. Used when the recorder is torn down.
             */
            recorderTargetService.clear = function () {
                captures = {};
                order = [];
                currentToken = null;
            };

            /**
             * Work out what a finished capture should be saved as.
             *
             * @param  {string} token         : capture the upload belongs to
             * @param  {Array}  allGuests     : the current recipient list
             * @param  {Array}  selectedGuests: the live selection
             * @return {Object}
             *   known      : the capture is still on record. When false the
             *                caller has nothing to go on and should fall back to
             *                live UI state, as it did before captures existed
             *   isPersonal : what the capture was recorded as -- null when the
             *                capture is unknown
             *   guests     : recipients to attach to (the live selection when
             *                the pin can't decide)
             *   resolved   : the recipients came from the pin rather than the
             *                fallback
             *   drifted    : the selection moved while this capture ran. Only
             *                meaningful for the capture on screen
             *   isCurrent  : this is the capture the recorder is showing
             */
            recorderTargetService.resolveTargets = function (token, allGuests, selectedGuests) {
                var selected = selectedGuests || [];
                var selectedIds = mapIds(selected);

                // strictly the capture named by the token: an upload that
                // arrives without one is unidentified, and must not inherit
                // whatever capture happens to be current
                var capture = captures[token];
                var isCurrent = !token || token === currentToken;

                var result = {
                    known: !!capture,
                    isPersonal: capture ? capture.isPersonal : null,
                    guests: selected,
                    resolved: false,
                    drifted: false,
                    isCurrent: isCurrent,
                    pinnedIds: capture ? capture.guestIds.slice() : [],
                    selectedIds: selectedIds,
                };

                if (!capture || !capture.isPersonal || !capture.guestIds.length) {
                    return result;
                }

                // resolve against the current list: getGuests() replaces the
                // guest objects, and can drop them entirely
                var guests = [];
                angular.forEach(allGuests, function (guest) {
                    if (capture.guestIds.indexOf(guest.id) >= 0) {
                        guests.push(guest);
                    }
                });

                if (!guests.length) {
                    return result;
                }

                result.guests = guests;
                result.resolved = true;

                // an older capture is expected not to match the current
                // selection, so only the capture on screen can have drifted
                result.drifted = isCurrent && (selectedIds.length !== capture.guestIds.length
                    || capture.guestIds.some(function (id) {
                        return selectedIds.indexOf(id) < 0;
                    }));

                return result;
            };

            return recorderTargetService;
        }]);
}());
